export type UserRole = 'player' | 'admin'

export interface Profile {
  id: string
  user_id: string
  username: string | null
  role: UserRole
  created_at: string
}

export type ProfileUpdate = Pick<Profile, 'username'>

// World types
export type WorldStatus = 'setup' | 'active' | 'paused' | 'completed'

/**
 * World settings for election configuration and other world-level parameters
 */
export interface WorldSettings {
  /** Tick number for the first election (default: 16) */
  first_election_tick?: number
  /** Length of campaign period in ticks (default: 4) */
  campaign_length?: number
  /** Whether early elections can be triggered (default: true) */
  allow_early_elections?: boolean
  /** Additional custom settings */
  [key: string]: unknown
}

export interface World {
  id: string
  name: string
  created_at: string
  tick_interval_hours: number
  current_tick: number
  status: WorldStatus
  settings: WorldSettings
}

export type WorldCreate = Pick<World, 'name'> &
  Partial<Pick<World, 'tick_interval_hours' | 'status' | 'settings'>>

export type WorldUpdate = Partial<
  Pick<World, 'name' | 'tick_interval_hours' | 'status' | 'settings'>
>

// Nation types
export type GovernmentType = 'parliamentary' | 'semi_presidential' | 'presidential'
export type ElectoralSystem = 'nationwide_pr' | 'regional_pr' | 'mixed'
export type JudicialReviewStrength = 'none' | 'weak' | 'strong'
export type MediaFreedom = 'free' | 'state_influenced' | 'polarized' | 'censored'

export interface Nation {
  id: string
  world_id: string
  name: string
  government_type: GovernmentType
  electoral_system: ElectoralSystem
  term_length_ticks: number
  threshold_percent: number
  max_parties: number
  confidence_votes_enabled: boolean
  judicial_review_strength: JudicialReviewStrength
  media_freedom: MediaFreedom
  campaign_restrictions: Record<string, unknown>
  current_government_id: string | null
  next_election_tick: number | null
}

export type NationCreate = Pick<Nation, 'world_id' | 'name'> &
  Partial<Omit<Nation, 'id' | 'world_id' | 'name'>>

export type NationUpdate = Partial<Omit<Nation, 'id' | 'world_id'>>

// Region types
export type RegionType = 'coastal' | 'mountain' | 'plains' | 'forest' | 'urban_core'

export interface Region {
  id: string
  nation_id: string
  name: string
  region_type: RegionType
  seat_count: number
  population: number
  economic_profile: Record<string, unknown>
}

export type RegionCreate = Pick<Region, 'nation_id' | 'name'> &
  Partial<Omit<Region, 'id' | 'nation_id' | 'name'>>

export type RegionUpdate = Partial<Omit<Region, 'id' | 'nation_id'>>

// District types
export type SettlementType = 'rural' | 'suburban' | 'urban'

export interface District {
  id: string
  region_id: string
  name: string
  settlement_type: SettlementType
  population: number
  seat_count: number
}

export type DistrictCreate = Pick<District, 'region_id' | 'name'> &
  Partial<Omit<District, 'id' | 'region_id' | 'name'>>

export type DistrictUpdate = Partial<Omit<District, 'id' | 'region_id'>>

// Cohort types
export type AgeBracket = 'youth' | 'young_adult' | 'middle_aged' | 'senior'
export type EducationLevel = 'low' | 'medium' | 'high'
export type IncomeLevel = 'low' | 'middle' | 'high'

export type IdeologyVector = [number, number, number, number, number, number, number]

export interface Cohort {
  id: string
  district_id: string
  age_bracket: AgeBracket
  education_level: EducationLevel
  income_level: IncomeLevel
  religion: string
  population: number
  ideology_vector: IdeologyVector
  salience_weights: IdeologyVector
  base_turnout: number
  current_turnout_modifier: number
}

export type CohortCreate = Omit<Cohort, 'id'>

export type CohortUpdate = Partial<Omit<Cohort, 'id' | 'district_id'>>

// Party types
export interface Party {
  id: string
  nation_id: string
  user_id: string
  name: string
  abbreviation: string
  color: string
  slogan: string
  ideology_vector: IdeologyVector
  money: number
  action_points: number
  prestige: number
  momentum: number
  traits: string[]
  created_tick: number
  is_active: boolean
}

// For creating a new party (player provides these)
export interface CreatePartyInput {
  nation_id: string
  name: string
  abbreviation: string
  color: string
  slogan?: string
  ideology_vector: IdeologyVector
}

// For updating party basics (player can edit)
export interface UpdatePartyBasicsInput {
  name?: string
  abbreviation?: string
  color?: string
  slogan?: string
}

// For updating party resources (game-controlled)
export interface UpdatePartyResourcesInput {
  money?: number
  action_points?: number
  prestige?: number
  momentum?: number
}

// Party with joined nation data
export interface PartyWithDetails extends Party {
  nation: {
    id: string
    name: string
    world_id: string
    next_election_tick: number | null
    term_length_ticks: number
  }
}

// Result type for canCreatePartyInNation check
export interface CanCreatePartyResult {
  allowed: boolean
  reason?: string
}

// Nation with world and party info for join flow
export interface JoinableNation {
  nation: Nation
  world: {
    id: string
    name: string
    status: WorldStatus
  }
  partyCount: number
  userParty: Party | null
}

// Party with full nation and world info for dashboard
export interface PartyWithNationWorld extends Party {
  nation: Nation
  world: {
    id: string
    name: string
    status: WorldStatus
  }
}

// Party District State types
export interface PartyDistrictState {
  id: string
  party_id: string
  district_id: string
  recognition: number // 0-100, how well-known the party is in this district
  organization_strength: number // 0-100, ground game infrastructure
}

// For updating party district state (game mechanics only)
export interface UpdatePartyDistrictStateInput {
  recognition?: number
  organization_strength?: number
}

// For bulk updates during tick processing
export interface BulkPartyDistrictStateUpdate {
  party_id: string
  district_id: string
  recognition?: number
  organization_strength?: number
}

// Party district state with joined district details
export interface PartyDistrictStateWithDistrict extends PartyDistrictState {
  district: {
    id: string
    name: string
    settlement_type: SettlementType
    population: number
    region: {
      id: string
      name: string
    }
  }
}

// Party district state with joined party details (for competitive analysis)
export interface PartyDistrictStateWithParty extends PartyDistrictState {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
}

// Party district state with both district and party details (for nation-wide view)
export interface PartyDistrictStateWithAll extends PartyDistrictState {
  district: {
    id: string
    name: string
    settlement_type: SettlementType
    population: number
    region: {
      id: string
      name: string
    }
  }
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
}

// Candidate types
export type CandidateRole =
  | 'none' // Not assigned to any role
  | 'regional_candidate' // Running in a specific district
  | 'list_candidate' // On party list for PR allocation
  | 'party_leader' // Leader of the party (one per party)
  | 'minister' // Serving in cabinet (Phase 4)
  | 'chair' // Committee chair or similar (future)

export type RecruitmentSource =
  | 'youth_wing' // Young, idealistic, loyal but inexperienced
  | 'union' // Labor-aligned, strong with workers
  | 'business' // Business-aligned, fundraising connections
  | 'activist' // Passionate, high charisma, volatile
  | 'think_tank' // Policy experts, high admin skill
  | 'independent' // Experienced politicians, may be ambitious
  | 'poached' // Recruited from another party

export interface Candidate {
  id: string
  party_id: string

  // Identity
  name: string
  age: number

  // Ideology (personal, may differ from party)
  ideology_vector: IdeologyVector

  // Status
  loyalty: number // 0-100, how loyal to party
  recognition: number // 0-100, how well-known

  // Skills
  charisma: number // 0-100, affects campaigning
  admin_skill: number // 0-100, affects governing
  debate_skill: number // 0-100, affects debates/attacks

  // Traits
  scandal_prone: number // 0-100, likelihood of scandals
  ambition: number // 0-100, may cause loyalty issues
  traits: string[] // Additional traits (future)

  // Role
  role: CandidateRole
  assigned_district_id: string | null

  // Metadata
  recruited_tick: number
  recruitment_source: RecruitmentSource
  is_active: boolean
}

// For creating a new candidate (via recruitment action)
export interface CreateCandidateInput {
  party_id: string
  name: string
  age: number
  ideology_vector: IdeologyVector
  recruitment_source: RecruitmentSource
  loyalty?: number
  recognition?: number
  charisma?: number
  admin_skill?: number
  debate_skill?: number
  scandal_prone?: number
  ambition?: number
  recruited_tick?: number
}

// For updating candidate assignment (player-controlled)
export interface UpdateCandidateAssignmentInput {
  role?: CandidateRole
  assigned_district_id?: string | null
}

// For updating candidate stats (game mechanics)
export interface UpdateCandidateStatsInput {
  loyalty?: number
  recognition?: number
  charisma?: number
  admin_skill?: number
  debate_skill?: number
  scandal_prone?: number
  ambition?: number
  traits?: string[]
  is_active?: boolean
}

// Candidate with joined party data
export interface CandidateWithParty extends Candidate {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
    ideology_vector: IdeologyVector
  }
}

// Candidate with joined district data (when assigned)
export interface CandidateWithDistrict extends Candidate {
  district: {
    id: string
    name: string
    settlement_type: SettlementType
    population: number
    region: {
      id: string
      name: string
    }
  } | null
}

// Candidate with full context (for detailed views)
export interface CandidateWithDetails extends Candidate {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
    ideology_vector: IdeologyVector
  }
  district: {
    id: string
    name: string
    settlement_type: SettlementType
    population: number
    region: {
      id: string
      name: string
    }
  } | null
}

// Cohort Party Loyalty types
export interface CohortPartyLoyalty {
  id: string
  cohort_id: string
  party_id: string
  loyalty_score: number // 0-100, accumulated attachment to party
  last_voted_tick: number | null // tick when cohort last voted for this party
  created_at: string
}

// For updating loyalty (game mechanics only, during tick processing)
export interface UpdateCohortPartyLoyaltyInput {
  loyalty_score?: number
  last_voted_tick?: number | null
}

// For bulk updates during election tick processing
export interface BulkCohortPartyLoyaltyUpdate {
  cohort_id: string
  party_id: string
  loyalty_score?: number
  last_voted_tick?: number | null
}

// Cohort party loyalty with joined cohort details
export interface CohortPartyLoyaltyWithCohort extends CohortPartyLoyalty {
  cohort: {
    id: string
    age_bracket: AgeBracket
    education_level: EducationLevel
    income_level: IncomeLevel
    religion: string
    population: number
    district: {
      id: string
      name: string
      settlement_type: SettlementType
    }
  }
}

// Cohort party loyalty with joined party details
export interface CohortPartyLoyaltyWithParty extends CohortPartyLoyalty {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
}

// Aggregated loyalty stats for a party (useful for dashboards)
export interface PartyLoyaltyStats {
  party_id: string
  total_loyal_population: number // Sum of population where loyalty > threshold
  average_loyalty: number // Average loyalty across all cohorts
  cohort_count: number // Number of cohorts with any loyalty
  stronghold_count: number // Number of cohorts with high loyalty (e.g., > 50)
}

// Tick types
export type TickStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface Tick {
  id: string
  world_id: string
  tick_number: number
  started_at: string
  completed_at: string | null
  status: TickStatus
}

// For creating a new tick (system/tick processor only)
export interface CreateTickInput {
  world_id: string
  tick_number: number
  status?: TickStatus
}

// For updating tick status (system/tick processor only)
export interface UpdateTickInput {
  started_at?: string
  completed_at?: string | null
  status?: TickStatus
}

// Tick with joined world data
export interface TickWithWorld extends Tick {
  world: {
    id: string
    name: string
    status: WorldStatus
  }
}

// Party Action types
export type ActionType =
  | 'campaign'    // Increase visibility/support in districts
  | 'fundraise'   // Raise money
  | 'poll'        // Gather information about voter sentiment
  | 'recruit'     // Find new candidates
  | 'attack'      // Negative campaigning against opponents
  | 'pr'          // Public relations, damage control
  | 'internal'    // Party management (training, ideology shifts)
  | 'organize'    // Build ground game infrastructure

export type TargetType =
  | 'district'    // Single district target
  | 'region'      // Single region target
  | 'cohort'      // Specific demographic cohort
  | 'party'       // Target another party
  | 'candidate'   // Target a specific candidate
  | 'national'    // Nation-wide action (no specific target)

export type ActionStatus =
  | 'queued'      // Submitted, waiting for tick processing
  | 'resolved'    // Processed during tick
  | 'cancelled'   // Player cancelled before processing

export interface PartyAction {
  id: string
  party_id: string
  tick: number
  action_type: ActionType
  action_subtype: string | null
  target_type: TargetType
  target_id: string | null
  action_points_spent: number
  parameters: Record<string, unknown>
  status: ActionStatus
  results: Record<string, unknown> | null
  created_at: string
}

// For creating a new action (player submission)
export interface CreatePartyActionInput {
  party_id: string
  tick: number
  action_type: ActionType
  action_subtype?: string
  target_type: TargetType
  target_id?: string | null
  action_points_spent: number
  parameters?: Record<string, unknown>
}

// For updating action status (cancel or system update)
export interface UpdatePartyActionInput {
  status?: ActionStatus
  results?: Record<string, unknown>
}

// Party action with joined party data
export interface PartyActionWithParty extends PartyAction {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
}

// Party action with target details (for display)
export interface PartyActionWithTarget extends PartyAction {
  target: {
    id: string
    name: string
    type: TargetType
  } | null
}

// Party action with full context
export interface PartyActionWithDetails extends PartyAction {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
  target: {
    id: string
    name: string
    type: TargetType
  } | null
}

// Polling types
export type PollType = 'national' | 'regional' | 'district'
export type DetailLevel = 'basic' | 'detailed'

export interface PartyStanding {
  party_id: string
  party_name: string
  party_color: string
  vote_share: number
  vote_share_low: number
  vote_share_high: number
}

export interface CohortBreakdown {
  cohort_description: string
  leading_party_id: string
  leading_party_name: string
  margin: number
}

export interface TopIssue {
  axis_index: number
  axis_name: string
  importance: number
  favored_direction: string
}

export interface SwingCohort {
  cohort_description: string
  volatility: number
}

export interface BasicPollResults {
  party_standings: PartyStanding[]
  total_respondents: number
  methodology_note: string
}

export interface DetailedPollResults extends BasicPollResults {
  cohort_breakdown: CohortBreakdown[]
  top_issues: TopIssue[]
  swing_cohorts: SwingCohort[]
}

export interface PollingSnapshot {
  id: string
  nation_id: string
  party_id: string
  tick: number
  poll_type: PollType
  target_id: string | null
  results: BasicPollResults | DetailedPollResults
  sample_size: number
  margin_of_error: number
  detail_level: DetailLevel
  created_at: string
}

// For creating a new polling snapshot (service role only)
export interface CreatePollingSnapshotInput {
  nation_id: string
  party_id: string
  tick: number
  poll_type: PollType
  target_id?: string | null
  results: BasicPollResults | DetailedPollResults
  sample_size: number
  margin_of_error: number
  detail_level: DetailLevel
}

// Polling snapshot with joined party data
export interface PollingSnapshotWithParty extends PollingSnapshot {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
}

// Polling snapshot with target details (for display)
export interface PollingSnapshotWithTarget extends PollingSnapshot {
  target: {
    id: string
    name: string
    type: PollType
  } | null
}

// Type guard to check if results are detailed
export function isDetailedPollResults(
  results: BasicPollResults | DetailedPollResults
): results is DetailedPollResults {
  return 'cohort_breakdown' in results
}

// Scandal types
export type ScandalType = 'corruption' | 'hypocrisy' | 'personal_misconduct' | 'policy_failure'
export type ScandalSeverity = 'minor' | 'moderate' | 'major'
export type ScandalStatus = 'active' | 'fading' | 'resolved'

export interface Scandal {
  id: string
  target_party_id: string
  target_candidate_id: string | null
  scandal_type: ScandalType
  severity: ScandalSeverity
  discovered_tick: number
  source_party_id: string
  source_revealed: boolean
  status: ScandalStatus
  effects_applied: boolean
  expires_tick: number // tick when scandal naturally resolves
  created_at: string
}

// For creating a new scandal (via opposition research)
export interface CreateScandalInput {
  target_party_id: string
  target_candidate_id?: string | null
  scandal_type: ScandalType
  severity: ScandalSeverity
  discovered_tick: number
  source_party_id: string
  expires_tick: number
}

// For updating scandal status (game mechanics)
export interface UpdateScandalInput {
  status?: ScandalStatus
  source_revealed?: boolean
  effects_applied?: boolean
}

// Scandal with joined party data
export interface ScandalWithParty extends Scandal {
  target_party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
  source_party?: {
    id: string
    name: string
    abbreviation: string
    color: string
  } | null // Only populated if source_revealed is true
}

// Scandal with joined candidate data (if scandal targets a candidate)
export interface ScandalWithCandidate extends Scandal {
  target_candidate: {
    id: string
    name: string
    role: CandidateRole
  } | null
}

// Faction types
export type FactionStatus = 'dormant' | 'active' | 'demanding' | 'threatening' | 'defecting'

export type FactionDemandType =
  | 'cabinet_role'
  | 'policy_shift'
  | 'candidate_promotion'
  | 'bill_support'
  | 'leadership_change'

export interface Faction {
  id: string
  party_id: string
  name: string
  ideology_vector: IdeologyVector | null
  influence: number // 0-100, faction power within party
  satisfaction: number // 0-100, current satisfaction level
  leader_candidate_id: string | null
  status: FactionStatus
  formed_tick: number
  last_demand_tick: number | null
  demand_type: FactionDemandType | null
  demand_details: Record<string, unknown> | null
  created_at: string
}

// For creating a new faction (game mechanics only)
export interface CreateFactionInput {
  party_id: string
  name: string
  formed_tick: number
  ideology_vector?: IdeologyVector | null
  leader_candidate_id?: string | null
  influence?: number
  satisfaction?: number
  status?: FactionStatus
}

// For updating faction (name by player, other fields by system)
export interface UpdateFactionInput {
  name?: string
  ideology_vector?: IdeologyVector | null
  influence?: number
  satisfaction?: number
  leader_candidate_id?: string | null
  status?: FactionStatus
  last_demand_tick?: number | null
  demand_type?: FactionDemandType | null
  demand_details?: Record<string, unknown> | null
}

// Faction with joined party data
export interface FactionWithParty extends Faction {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
}

// Faction with leader details
export interface FactionWithLeader extends Faction {
  leader: {
    id: string
    name: string
    recognition: number
    loyalty: number
  } | null
}

// Faction membership types
export interface FactionMembership {
  id: string
  faction_id: string
  candidate_id: string
  joined_tick: number
  commitment_level: number // 0-100, how committed to faction
  will_defect_with_faction: boolean
  created_at: string
}

// For creating a new faction membership (game mechanics only)
export interface CreateFactionMembershipInput {
  faction_id: string
  candidate_id: string
  joined_tick: number
  commitment_level?: number
  will_defect_with_faction?: boolean
}

// For updating faction membership (game mechanics only)
export interface UpdateFactionMembershipInput {
  commitment_level?: number
  will_defect_with_faction?: boolean
}

// Extended type with joined candidate data for UI display
export interface FactionMembershipWithCandidate extends FactionMembership {
  candidate: {
    id: string
    name: string
    role: CandidateRole
    recognition: number
    loyalty: number
    ideology_vector: IdeologyVector
  }
}

// Faction membership with joined faction data
export interface FactionMembershipWithFaction extends FactionMembership {
  faction: {
    id: string
    name: string
    status: FactionStatus
    influence: number
    party_id: string
  }
}

// Extended faction type with members
export interface FactionWithMembers extends Faction {
  members: FactionMembershipWithCandidate[]
  member_count: number
}

// Full faction details for display
export interface FactionWithDetails extends Faction {
  party: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
  leader: {
    id: string
    name: string
    recognition: number
    loyalty: number
  } | null
  members: FactionMembershipWithCandidate[]
  member_count: number
}

// Summary type for dashboard display
export interface FactionSummary {
  id: string
  name: string
  status: FactionStatus
  influence: number
  satisfaction: number
  member_count: number
  has_demand: boolean
  leader_name: string | null
}

// For bulk operations during tick processing
export interface FactionSatisfactionUpdate {
  factionId: string
  change: number
}

// ============================================================================
// Faction Formation Types
// ============================================================================

/**
 * A position on the global 9x9 grid (0-8 for both x and y)
 * Combines quadrant (0-2) and sub-position (0-2) into a single coordinate
 */
export interface GlobalGridPosition {
  x: number // 0-8 (quadrantX * 3 + subX)
  y: number // 0-8 (quadrantY * 3 + subY)
}

/**
 * A candidate with their calculated grid position
 */
export interface CandidateWithPosition {
  candidate: Candidate
  gridPosition: GlobalGridPosition
}

/**
 * A cluster of divergent candidates who are adjacent to each other
 */
export interface CandidateCluster {
  candidates: CandidateWithPosition[]
  centerIdeology: IdeologyVector
  centerGridPosition: GlobalGridPosition
  suggestedName: string
}

/**
 * Result of the faction detection and formation process
 */
export interface FactionFormationResult {
  partyId: string
  partyGridPosition: GlobalGridPosition
  totalCandidates: number
  divergentCandidates: number
  clustersFound: number
  factionsCreated: number
  factionsUpdated: number
  factionsDissolved: number
  factions: Faction[]
}

// ============================================================================
// Faction Satisfaction Types
// ============================================================================

/**
 * Reason for a faction satisfaction change
 */
export type SatisfactionChangeReason =
  | 'ideology_shift'
  | 'bill_passed'
  | 'bill_failed'
  | 'role_assignment'
  | 'internal_action'
  | 'demand_met'
  | 'demand_partially_met'
  | 'demand_rejected'
  | 'demand_ignored'
  | 'member_expelled'
  | 'leader_lost'
  | 'passive_decay'
  | 'other'

/**
 * Event record for a satisfaction change
 */
export interface SatisfactionChangeEvent {
  factionId: string
  previousSatisfaction: number
  change: number
  newSatisfaction: number
  reason: SatisfactionChangeReason
  details?: string
  previousStatus: FactionStatus
  newStatus: FactionStatus
  timestamp: string
}

/**
 * Result of passive satisfaction decay processing
 */
export interface SatisfactionDecayResult {
  factionId: string
  factionName: string
  partyId: string
  previousSatisfaction: number
  decay: number
  newSatisfaction: number
  ticksWithDemand: number
  statusChange?: { from: FactionStatus; to: FactionStatus }
}

/**
 * Effect of a bill on ideology
 */
export interface BillIdeologyEffect {
  axis: number // 0-6 index into ideology vector
  direction: number // positive = increase, negative = decrease
  magnitude: number // strength of effect
}

/**
 * Type of role change for satisfaction calculations
 */
export type RoleChangeType =
  | 'appointed_minister'
  | 'removed_minister'
  | 'assigned_regional'
  | 'removed_regional'
  | 'passed_over'
  | 'promoted'
  | 'demoted'

/**
 * A role change event for satisfaction calculation
 */
export interface RoleChange {
  candidateId: string
  changeType: RoleChangeType
  roleName?: string // e.g., "Finance Minister"
}

// ============================================================================
// Faction Demand Detail Types
// ============================================================================

/**
 * Details for a cabinet_role demand.
 * Faction wants one of their members appointed to a cabinet position.
 */
export interface CabinetRoleDemandDetails {
  type: 'cabinet_role'
  /** Faction member IDs, ordered by preference */
  preferred_candidates: string[]
  /** True if any ministry is acceptable */
  any_ministry: boolean
  /** Specific ministries if faction has preferences */
  preferred_ministries?: string[]
}

/**
 * Details for a policy_shift demand.
 * Faction wants party ideology to move on a specific axis.
 */
export interface PolicyShiftDemandDetails {
  type: 'policy_shift'
  /** Ideology axis index (0-6) */
  axis: number
  /** Human-readable axis name */
  axis_name: string
  /** Direction faction wants party to move */
  direction: 'increase' | 'decrease'
  /** Current party position on this axis */
  current_party_position: number
  /** Faction's position on this axis */
  faction_position: number
  /** Minimum shift required to satisfy demand */
  minimum_shift: number
}

/**
 * Details for a candidate_promotion demand.
 * Faction wants a specific candidate given a more prominent role.
 */
export interface CandidatePromotionDemandDetails {
  type: 'candidate_promotion'
  /** ID of candidate to promote */
  candidate_id: string
  /** Name of candidate for display */
  candidate_name: string
  /** Acceptable roles that would satisfy the demand */
  acceptable_roles: string[]
  /** Preferred district for regional candidate assignment */
  preferred_district_id?: string
}

/**
 * Details for a bill_support demand.
 * Faction wants party to vote a certain way on bills in a category.
 */
export interface BillSupportDemandDetails {
  type: 'bill_support'
  /** Category of bills (e.g., 'economic', 'social') */
  bill_category: string
  /** Whether faction wants support or opposition */
  stance: 'support' | 'oppose'
  /** Ideology axis this relates to */
  ideology_axis: number
  /** Human-readable explanation */
  reasoning: string
}

/**
 * Details for a leadership_change demand.
 * Faction has lost confidence in party leadership (extreme demand).
 */
export interface LeadershipChangeDemandDetails {
  type: 'leadership_change'
  /** Reason for the demand */
  reason: string
  /** Faction members who could serve as replacements */
  preferred_successor_ids?: string[]
}

/**
 * Union type for all faction demand details
 */
export type FactionDemandDetails =
  | CabinetRoleDemandDetails
  | PolicyShiftDemandDetails
  | CandidatePromotionDemandDetails
  | BillSupportDemandDetails
  | LeadershipChangeDemandDetails

/**
 * Status of a demand resolution check
 */
export type DemandResolutionStatus = 'met' | 'partially_met' | 'not_met' | 'rejected'

/**
 * Result of checking if a demand has been met
 */
export interface DemandResolution {
  /** Resolution status */
  status: DemandResolutionStatus
  /** Satisfaction change to apply */
  satisfactionChange: number
  /** Human-readable explanation */
  message: string
}

/**
 * Result of processing demand deadlines for a faction
 */
export interface DemandDeadlineResult {
  factionId: string
  factionName: string
  partyId: string
  demandType: FactionDemandType
  /** Ticks past the deadline (negative if still time remaining) */
  ticksOverdue: number
  previousStatus: FactionStatus
  newStatus: FactionStatus
  /** Whether the faction escalated this tick */
  escalated: boolean
}

// ============================================================================
// Faction Defection Types
// ============================================================================

/**
 * Result of the full defection process for a faction
 */
export interface DefectionProcessResult {
  /** ID of the defecting faction */
  factionId: string
  /** Name of the defecting faction */
  factionName: string
  /** ID of the original party */
  partyId: string
  /** Name of the original party */
  partyName: string
  /** Total number of faction members */
  totalMembers: number
  /** Number of members who defected */
  defectorCount: number
  /** Number of members who stayed */
  stayedCount: number
  /** Individual results for each defector */
  defectors: IndividualDefectionResult[]
  /** Consequences applied to original party */
  originalPartyConsequences: OriginalPartyConsequences
  /** Consequences applied to receiving parties (party ID -> consequences) */
  receivingPartyConsequences: Map<string, ReceivingPartyConsequences>
  /** Generated event records */
  events: DefectionEvent[]
  /** Whether government instability was triggered */
  governmentInstability: GovernmentInstabilityResult | null
  /** Whether faction was cleaned up (dissolved) */
  factionDissolved: boolean
}

/**
 * Result for an individual member's defection decision
 */
export interface IndividualDefectionResult {
  /** ID of the candidate */
  candidateId: string
  /** Name of the candidate */
  candidateName: string
  /** Whether they defected */
  defected: boolean
  /** The calculated defection probability */
  probability: number
  /** The random roll that determined defection */
  roll: number
  /** Destination: party ID, party name, or 'independent' */
  destination: DefectorDestination | null
  /** Their role before defection */
  previousRole: CandidateRole
  /** Whether they were a minister */
  wasMinister: boolean
  /** Whether they were the faction leader */
  wasFactionLeader: boolean
  /** Their assigned district ID before defection (if any) */
  previousDistrictId: string | null
}

/**
 * Destination for a defector
 */
export interface DefectorDestination {
  /** Type of destination */
  type: 'party' | 'independent'
  /** Party ID if joining a party */
  partyId?: string
  /** Party name if joining a party */
  partyName?: string
  /** Ideology distance to destination party */
  ideologyDistance?: number
}

/**
 * Consequences applied to the original party after defection
 */
export interface OriginalPartyConsequences {
  /** Prestige change (typically negative) */
  prestigeChange: number
  /** Number of remaining candidates affected */
  remainingCandidatesAffected: number
  /** Loyalty change for remaining candidates */
  loyaltyChange: number
  /** Districts where recognition was affected */
  affectedDistricts: DistrictRecognitionChange[]
}

/**
 * Recognition change in a specific district
 */
export interface DistrictRecognitionChange {
  districtId: string
  districtName: string
  recognitionChange: number
}

/**
 * Consequences applied to a receiving party
 */
export interface ReceivingPartyConsequences {
  partyId: string
  partyName: string
  /** Prestige change (typically positive for notable defectors) */
  prestigeChange: number
  /** Recognition gains in districts */
  recognitionGains: DistrictRecognitionChange[]
  /** Candidates received */
  candidatesReceived: string[]
}

/**
 * Result of a candidate transfer between parties
 */
export interface TransferResult {
  /** ID of the transferred candidate */
  candidateId: string
  /** Whether the transfer succeeded */
  success: boolean
  /** Original party ID */
  fromPartyId: string
  /** New party ID (or null if became independent) */
  toPartyId: string | null
  /** New loyalty value */
  newLoyalty: number
  /** Error message if failed */
  error?: string
}

/**
 * Result of government instability check
 */
export interface GovernmentInstabilityResult {
  /** Whether instability was triggered */
  triggered: boolean
  /** Reason for instability */
  reason: string
  /** ID of the minister who defected */
  ministerId?: string
  /** Name of the minister who defected */
  ministerName?: string
  /** Severity of the instability */
  severity: 'minor' | 'moderate' | 'major' | 'crisis'
}

/**
 * A defection event for the news/events system
 */
export interface DefectionEvent {
  /** Unique event ID (generated) */
  id?: string
  /** Type of event */
  type: 'defection'
  /** Severity level */
  severity: 'minor' | 'major' | 'crisis'
  /** Event title */
  title: string
  /** Detailed description */
  description: string
  /** Nation ID where event occurred */
  nationId: string
  /** Party ID of original party */
  originalPartyId: string
  /** Party ID of receiving party (if applicable) */
  receivingPartyId?: string
  /** Candidate IDs involved */
  candidateIds: string[]
  /** Tick when event occurred */
  tick: number
  /** Visibility of event */
  visibility: 'public' | 'party_only'
}

/**
 * Input for calculating defection probability
 */
export interface DefectionProbabilityInput {
  /** Whether member indicated they would defect with faction */
  willDefectWithFaction: boolean
  /** Member's commitment level (0-100) */
  commitmentLevel: number
  /** Candidate's loyalty to party (0-100) */
  candidateLoyalty: number
  /** Candidate's current role */
  candidateRole: CandidateRole
  /** Whether this candidate is the faction leader */
  isFactionLeader: boolean
  /** Candidate's ambition (0-100) */
  candidateAmbition: number
}

/**
 * A compatible party for defector destination
 */
export interface CompatibleParty {
  partyId: string
  partyName: string
  ideologyDistance: number
  prestige: number
}

/**
 * Result of finding compatible parties
 */
export interface CompatiblePartiesResult {
  /** Compatible parties sorted by distance (closest first) */
  parties: CompatibleParty[]
  /** Original party ID (excluded from results) */
  excludedPartyId: string
  /** Candidate's ideology vector */
  candidateIdeology: IdeologyVector
}

/**
 * Faction cleanup result
 */
export interface FactionCleanupResult {
  factionId: string
  /** Whether faction was deleted or marked dissolved */
  action: 'deleted' | 'dissolved'
  /** Number of memberships removed */
  membershipsRemoved: number
  /** Whether any demands were cleared */
  demandsCleared: boolean
}

// ============================================================================
// State Update Types (Step 3.35)
// ============================================================================

/**
 * Result of momentum decay for a single party
 */
export interface MomentumDecayResult {
  partyId: string
  partyName: string
  previousMomentum: number
  newMomentum: number
  change: number
}

/**
 * Result of loyalty decay for a single candidate
 */
export interface LoyaltyDecayResult {
  candidateId: string
  candidateName: string
  partyId: string
  previousLoyalty: number
  newLoyalty: number
  change: number
}

/**
 * Result of action points regeneration for a single party
 */
export interface ActionPointsRegenResult {
  partyId: string
  partyName: string
  previousAP: number
  newAP: number
  calculation: {
    baseAP: number
    moneyBonus: number
    orgBonus: number
    prestigeBonus: number
    totalMoney: number
    totalOrgStrength: number
    prestige: number
  }
}

/**
 * A nation that has an election due this tick
 */
export interface ElectionDueResult {
  nationId: string
  nationName: string
  currentTick: number
  scheduledTick: number
  ticksOverdue: number
}

/**
 * Summary of all state updates performed during a tick
 */
export interface StateUpdateSummary {
  /** World ID that was updated */
  worldId: string
  /** Tick number when updates were performed */
  tickNumber: number

  /** Momentum decay results */
  momentumDecay: {
    partiesUpdated: number
    results: MomentumDecayResult[]
    totalDecay: number
  }

  /** Candidate loyalty decay results */
  loyaltyDecay: {
    candidatesUpdated: number
    results: LoyaltyDecayResult[]
    totalDecay: number
  }

  /** Faction satisfaction processing results (from step 3.25-3.32) */
  factionProcessing: {
    factionsProcessed: number
    satisfactionChanges: number
    statusChanges: number
  }

  /** Action points regeneration results */
  actionPointsRegen: {
    partiesUpdated: number
    results: ActionPointsRegenResult[]
    totalAPGenerated: number
  }

  /** Election check results */
  electionCheck: {
    nationsChecked: number
    electionsDue: ElectionDueResult[]
  }

  /** Processing metadata */
  processingTime: {
    momentumDecayMs: number
    loyaltyDecayMs: number
    factionProcessingMs: number
    apRegenMs: number
    electionCheckMs: number
    totalMs: number
  }
}

// ============================================================================
// Election Types (Step 3.38)
// ============================================================================

/**
 * Election lifecycle status
 * - scheduled: Election is planned but campaign hasn't started
 * - campaigning: Active campaign period (4 ticks before election by default)
 * - voting: Election day - votes being tallied (brief state during tick processing)
 * - completed: Election finished, results are final
 */
export type ElectionStatus = 'scheduled' | 'campaigning' | 'voting' | 'completed'

/**
 * Type of election
 * - legislative: Parliamentary/congressional elections
 * - executive: Presidential elections (where applicable by government type)
 */
export type ElectionType = 'legislative' | 'executive'

/**
 * A single party's result in an election
 */
export interface PartyElectionResult {
  party_id: string
  party_name: string
  party_color: string
  votes: number
  vote_share: number
  seats_won: number
  previous_seats: number
}

/**
 * Seat allocation breakdown by party
 */
export interface SeatAllocation {
  party_id: string
  party_name: string
  party_color: string
  seats: number
  change_from_previous: number
}

/**
 * Complete election results stored in the results JSONB column
 */
export interface ElectionResults {
  /** Total number of valid votes cast */
  total_votes: number
  /** Voter turnout as a percentage */
  turnout_percentage: number
  /** Total seats being contested */
  total_seats: number
  /** Per-party vote and seat results */
  party_results: PartyElectionResult[]
  /** Final seat distribution */
  seat_allocation: SeatAllocation[]
  /** Winning party ID (most seats) */
  winner_party_id: string | null
  /** Whether a majority was achieved */
  majority_achieved: boolean
  /** Tick when results were finalized */
  completed_tick: number
}

/**
 * Election record from the database
 */
export interface Election {
  id: string
  nation_id: string
  election_type: ElectionType
  scheduled_tick: number
  campaign_start_tick: number
  status: ElectionStatus
  is_early: boolean
  results: ElectionResults | null
  created_at: string
  updated_at: string
}

/**
 * Input for creating a new election
 */
export interface CreateElectionInput {
  nation_id: string
  election_type?: ElectionType
  scheduled_tick: number
  campaign_length?: number
  is_early?: boolean
}

/**
 * Input for updating election status
 */
export interface UpdateElectionStatusInput {
  status: ElectionStatus
  results?: ElectionResults
}

/**
 * Election with joined nation data
 */
export interface ElectionWithNation extends Election {
  nation: {
    id: string
    name: string
    world_id: string
    electoral_system: ElectoralSystem
    threshold_percent: number
  }
}

/**
 * Election phase for UI display and gameplay
 * More granular than status for UI/gameplay purposes
 */
export type ElectionPhase =
  | 'pre-campaign'    // Before campaign_start_tick
  | 'early-campaign'  // First half of campaign (2+ ticks until election)
  | 'late-campaign'   // Final stretch (1-2 ticks until election)
  | 'election-day'    // On scheduled_tick
  | 'post-election'   // After completion

/**
 * Summary of upcoming election for dashboard display
 */
export interface ElectionSummary {
  election_id: string
  nation_id: string
  nation_name: string
  election_type: ElectionType
  scheduled_tick: number
  campaign_start_tick: number
  status: ElectionStatus
  phase: ElectionPhase
  ticks_until_election: number
  ticks_until_campaign: number
  is_early: boolean
}

// Election Results Types (Step 3.40)
// ============================================================================

/**
 * Granular election result record from the database
 * Supports three levels:
 *   - District-level: district_id is set, region_id is NULL
 *   - Region-level: district_id is NULL, region_id is set
 *   - National-level: both district_id and region_id are NULL
 */
export interface ElectionResult {
  id: string
  election_id: string
  party_id: string
  district_id: string | null
  region_id: string | null
  vote_count: number
  vote_share: number
  seats_won: number
  turnout_count: number | null
  eligible_voters: number | null
  created_at: string
}

/**
 * Extended election result with joined data for UI display
 */
export interface ElectionResultWithDetails extends ElectionResult {
  party?: {
    id: string
    name: string
    abbreviation: string
    color: string
  }
  district?: {
    id: string
    name: string
    region_id: string
  }
  region?: {
    id: string
    name: string
  }
}

/**
 * Aggregated national election summary for dashboard display
 */
export interface NationalElectionSummary {
  election_id: string
  total_votes: number
  total_seats: number
  turnout_percentage: number
  party_results: PartyNationalResult[]
}

/**
 * A single party's national-level result
 */
export interface PartyNationalResult {
  party_id: string
  party_name: string
  party_abbreviation: string
  party_color: string
  total_votes: number
  vote_share: number
  seats_won: number
  seat_share: number
  /** seats_won / vote_share - measures proportionality */
  vote_to_seat_ratio: number
}

/**
 * District-level election summary with winner info
 */
export interface DistrictElectionSummary {
  district_id: string
  district_name: string
  region_name: string
  total_votes: number
  turnout_percentage: number
  winner_party_id: string
  winner_vote_share: number
  /** Difference between 1st and 2nd place vote share */
  margin: number
  party_results: ElectionResult[]
}

/**
 * Comparison of a party's results between two elections
 */
export interface PartyResultComparison {
  party_id: string
  party_name: string
  party_color: string
  current_votes: number
  current_share: number
  current_seats: number
  previous_votes: number
  previous_share: number
  previous_seats: number
  vote_change: number
  seat_change: number
}

/**
 * Input for creating an election result record
 */
export interface CreateElectionResultInput {
  election_id: string
  party_id: string
  district_id?: string | null
  region_id?: string | null
  vote_count: number
  vote_share: number
  seats_won?: number
  turnout_count?: number | null
  eligible_voters?: number | null
}

// Government Types (Step 3.45)
// ============================================================================

/**
 * Government formation type
 * - majority: Single party has majority of seats
 * - coalition: Multiple parties form government
 * - minority: Largest party governs without majority
 */
export type GovernmentFormationType = 'majority' | 'coalition' | 'minority'

/**
 * Government lifecycle status
 */
export type GovernmentStatusType = 'forming' | 'active' | 'dissolved' | 'caretaker'

/**
 * Government record from the database
 */
export interface Government {
  id: string
  nation_id: string
  election_id: string | null
  status: GovernmentStatusType
  government_type: GovernmentFormationType
  lead_party_id: string | null
  seats_held: number
  total_seats: number
  formed_tick: number
  dissolved_tick: number | null
  created_at: string
  updated_at: string
}

/**
 * Government with joined party and election data for display
 */
export interface GovernmentWithDetails extends Government {
  lead_party?: {
    id: string
    name: string
    abbreviation: string
    color: string
  } | null
  election?: {
    id: string
    scheduled_tick: number
    election_type: ElectionType
  } | null
}
